/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { colors, borderRadius, shadows, transitions } from '../../styles/theme';
import { ReactNode, ButtonHTMLAttributes } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'white' | 'outlineWhite';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
}

const baseStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  border-radius: ${borderRadius.lg};
  border: 2px solid transparent;
  transition: all ${transitions.fast};
  cursor: pointer;
  text-decoration: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 1.1em;
    height: 1.1em;
    flex-shrink: 0;
  }
`;

const sizeStyles = {
  sm: css`
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  `,
  md: css`
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  `,
  lg: css`
    padding: 0.875rem 2rem;
    font-size: 1.0625rem;
  `,
};

const variantStyles = {
  primary: css`
    background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%);
    color: white;
    box-shadow: ${shadows.md};

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[800]} 100%);
      box-shadow: ${shadows.lg};
      transform: translateY(-1px);
      color: white;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  secondary: css`
    background: linear-gradient(135deg, ${colors.secondary[500]} 0%, ${colors.secondary[600]} 100%);
    color: white;
    box-shadow: ${shadows.md};

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, ${colors.secondary[600]} 0%, ${colors.secondary[700]} 100%);
      box-shadow: ${shadows.lg};
      transform: translateY(-1px);
      color: white;
    }
  `,
  outline: css`
    background: transparent;
    color: ${colors.primary[700]};
    border-color: ${colors.primary[500]};

    &:hover:not(:disabled) {
      background: ${colors.primary[50]};
      border-color: ${colors.primary[600]};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${colors.text.secondary};

    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      color: ${colors.text.primary};
    }
  `,
  // Koyu/renkli zemin üzerinde kullanılır; metin rengi css prop ile değiştirilebilir
  white: css`
    background: white;
    color: ${colors.primary[700]};
    box-shadow: ${shadows.md};

    &:hover:not(:disabled) {
      background: ${colors.neutral[50]};
      box-shadow: ${shadows.lg};
      transform: translateY(-1px);
    }
  `,
  outlineWhite: css`
    background: transparent;
    color: white;
    border-color: rgba(255, 255, 255, 0.85);

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
      border-color: white;
      color: white;
    }
  `,
};

export function buttonStyles(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  fullWidth = false,
) {
  return [
    baseStyles,
    sizeStyles[size],
    variantStyles[variant],
    fullWidth && css`width: 100%;`,
  ];
}

export default function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  children,
  ...props
}: ButtonProps) {
  return (
    <button css={buttonStyles(variant, size, fullWidth)} {...props}>
      {children}
    </button>
  );
}

interface ButtonLinkProps {
  /** Site içi bağlantı */
  to?: string;
  /** Harici bağlantı (yeni sekmede açılır) */
  href?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}

// Buton görünümlü bağlantı - <a> içine <button> koymak yerine kullanılır
export function ButtonLink({
  to,
  href,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  children,
}: ButtonLinkProps) {
  const styles = buttonStyles(variant, size, fullWidth);

  if (to) {
    return (
      <Link to={to} css={styles} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" css={styles} className={className}>
      {children}
    </a>
  );
}
